import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, ShieldCheck, ShieldOff, Copy, CheckCircle, AlertTriangle } from 'lucide-react';

export default function TwoFactorSettings() {
  const { user, setup2FA, enable2FA, disable2FA, get2FAStatus } = useAuth();
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Setup state
  const [showSetup, setShowSetup] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  
  // Disable state
  const [showDisable, setShowDisable] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  useEffect(() => {
    const checkStatus = async () => {
      const result = await get2FAStatus();
      setIs2FAEnabled(result.enabled);
      setLoading(false);
    };
    checkStatus();
  }, [get2FAStatus]);

  const handleStartSetup = async () => {
    setLoading(true);
    const result = await setup2FA();
    
    if (result.success) {
      setSetupData(result.data);
      setShowSetup(true);
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  const handleVerifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setLoading(true);
    const result = await enable2FA(verificationCode);
    
    if (result.success) {
      setIs2FAEnabled(true);
      setShowRecoveryCodes(true);
      toast.success('Two-factor authentication enabled!');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  const handleDisable = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Please enter your current 6-digit code');
      return;
    }
    
    setLoading(true);
    const result = await disable2FA(disableCode);
    
    if (result.success) {
      setIs2FAEnabled(false);
      setShowDisable(false);
      setDisableCode('');
      setSetupData(null);
      toast.success('Two-factor authentication disabled');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
    toast.success('Copied to clipboard');
  };

  const handleCloseSetup = () => {
    setShowSetup(false);
    setShowRecoveryCodes(false);
    setVerificationCode('');
    setSetupData(null);
  };

  if (loading && !showSetup) {
    return (
      <div className="p-6 bg-white rounded-xl border border-[#E6E0D9]">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin"></div>
          <span className="text-[#5D524D]">Loading security settings...</span>
        </div>
      </div>
    );
  }

  // Recovery Codes View
  if (showRecoveryCodes && setupData) {
    return (
      <div className="p-6 bg-white rounded-xl border border-[#E6E0D9]" data-testid="2fa-recovery-codes">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#6B8E6B]/10 rounded-full flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-[#6B8E6B]" />
          </div>
          <div>
            <h3 className="font-heading text-lg text-[#2C2420]">2FA Enabled Successfully!</h3>
            <p className="text-sm text-[#5D524D]">Save your recovery codes</p>
          </div>
        </div>

        <div className="bg-[#FFF8E7] border border-[#D99A6C] rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#D99A6C] mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#2C2420]">Important: Save these codes now!</p>
              <p className="text-xs text-[#5D524D] mt-1">
                These codes can be used to access your account if you lose your authenticator. Each code can only be used once.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-6">
          {setupData.recovery_codes.map((code, index) => (
            <div 
              key={index}
              className="font-mono text-sm bg-[#F9F7F5] rounded px-3 py-2 text-center text-[#2C2420]"
            >
              {code}
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <Button
            onClick={() => copyToClipboard(setupData.recovery_codes.join('\n'))}
            variant="outline"
            className="flex-1"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy All
          </Button>
          <Button
            onClick={handleCloseSetup}
            className="caffeine-btn-primary flex-1"
          >
            I&apos;ve Saved My Codes
          </Button>
        </div>
      </div>
    );
  }

  // Setup View
  if (showSetup && setupData) {
    return (
      <div className="p-6 bg-white rounded-xl border border-[#E6E0D9]" data-testid="2fa-setup">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#4A3B32]/10 rounded-full flex items-center justify-center">
            <Shield className="w-5 h-5 text-[#4A3B32]" />
          </div>
          <div>
            <h3 className="font-heading text-lg text-[#2C2420]">Set Up Two-Factor Authentication</h3>
            <p className="text-sm text-[#5D524D]">Scan the QR code with your authenticator app</p>
          </div>
        </div>

        {/* QR Code */}
        <div className="flex justify-center mb-6">
          <div className="p-4 bg-white rounded-lg shadow-sm border border-[#E6E0D9]">
            <img 
              src={setupData.qr_code} 
              alt="2FA QR Code" 
              className="w-48 h-48"
              data-testid="2fa-qr-code"
            />
          </div>
        </div>

        {/* Manual Entry */}
        <div className="mb-6">
          <Label className="text-xs text-[#8C8078] uppercase tracking-wide">Manual Entry Key</Label>
          <div className="flex items-center gap-2 mt-1">
            <code className="flex-1 bg-[#F9F7F5] rounded px-3 py-2 text-sm font-mono text-[#2C2420] break-all">
              {setupData.secret}
            </code>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copyToClipboard(setupData.secret)}
            >
              {copiedSecret ? <CheckCircle className="w-4 h-4 text-[#6B8E6B]" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Verification */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="verification-code" className="text-[#2C2420]">Enter Verification Code</Label>
            <Input
              id="verification-code"
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="caffeine-input text-center text-xl tracking-widest mt-1"
              maxLength={6}
              data-testid="2fa-verification-input"
            />
            <p className="text-xs text-[#8C8078] mt-1">Enter the 6-digit code from your authenticator app</p>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => setShowSetup(false)}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleVerifyAndEnable}
              disabled={loading || verificationCode.length !== 6}
              className="caffeine-btn-primary flex-1"
              data-testid="enable-2fa-btn"
            >
              {loading ? 'Verifying...' : 'Enable 2FA'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Disable Confirmation View
  if (showDisable) {
    return (
      <div className="p-6 bg-white rounded-xl border border-[#E6E0D9]" data-testid="2fa-disable">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <ShieldOff className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-heading text-lg text-[#2C2420]">Disable Two-Factor Authentication</h3>
            <p className="text-sm text-[#5D524D]">Enter your current code to confirm</p>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800">
            <strong>Warning:</strong> Disabling 2FA will make your account less secure. Only do this if you&apos;re unable to access your authenticator app.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="disable-code" className="text-[#2C2420]">Current 2FA Code</Label>
            <Input
              id="disable-code"
              type="text"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="caffeine-input text-center text-xl tracking-widest mt-1"
              maxLength={6}
              data-testid="disable-2fa-code-input"
            />
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => { setShowDisable(false); setDisableCode(''); }}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDisable}
              disabled={loading || disableCode.length !== 6}
              variant="destructive"
              className="flex-1"
              data-testid="confirm-disable-2fa-btn"
            >
              {loading ? 'Disabling...' : 'Disable 2FA'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Main View
  return (
    <div className="p-6 bg-white rounded-xl border border-[#E6E0D9]" data-testid="2fa-settings">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            is2FAEnabled ? 'bg-[#6B8E6B]/10' : 'bg-[#4A3B32]/10'
          }`}>
            {is2FAEnabled ? (
              <ShieldCheck className="w-5 h-5 text-[#6B8E6B]" />
            ) : (
              <Shield className="w-5 h-5 text-[#4A3B32]" />
            )}
          </div>
          <div>
            <h3 className="font-heading text-lg text-[#2C2420]">Two-Factor Authentication</h3>
            <p className="text-sm text-[#5D524D]">
              {is2FAEnabled 
                ? 'Your account is protected with 2FA' 
                : 'Add an extra layer of security to your account'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {is2FAEnabled ? (
            <>
              <span className="px-3 py-1 bg-[#6B8E6B]/10 text-[#6B8E6B] text-sm font-medium rounded-full">
                Enabled
              </span>
              <Button
                onClick={() => setShowDisable(true)}
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="disable-2fa-start-btn"
              >
                Disable
              </Button>
            </>
          ) : (
            <Button
              onClick={handleStartSetup}
              disabled={loading}
              className="caffeine-btn-primary"
              data-testid="setup-2fa-btn"
            >
              {loading ? 'Loading...' : 'Set Up 2FA'}
            </Button>
          )}
        </div>
      </div>

      {!is2FAEnabled && (
        <div className="mt-4 pt-4 border-t border-[#E6E0D9]">
          <p className="text-sm text-[#8C8078]">
            Two-factor authentication adds an extra layer of security by requiring a code from your authenticator app when signing in. Compatible with Google Authenticator, Authy, Microsoft Authenticator, and other TOTP apps.
          </p>
        </div>
      )}
    </div>
  );
}
